import { message } from 'antd';
import { logger } from '@ui/utils/logger';
import { STORAGE_KEYS, TIME } from '../constants';

// ===========================
// Configuration
// ===========================
const MAX_CONVERSATIONS = 10; // Keep only the last 10 conversations
const STORAGE_QUOTA_ESTIMATE = 5 * 1024 * 1024; // 5MB (browser default for sessionStorage)
const WARNING_THRESHOLD = 0.8; // Warn at 80% usage
const EMERGENCY_THRESHOLD = 0.95; // Emergency cleanup at 95%

// Storage key prefixes (updated to use constants)
const CONVERSATION_KEY_PREFIXES = [
    'userMessage:',
    'correlation:',
    STORAGE_KEYS.TABS, // Also track tabs storage
];

// Cache invalidation interval (recalculate every 5 minutes)
const CACHE_INVALIDATION_INTERVAL = 5 * TIME.MINUTE_MS;

// ===========================
// Types
// ===========================
export interface StorageUsage {
    used: number;
    total: number;
    percentage: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
}

interface ConversationEntry {
    key: string;
    timestamp: number;
    size: number;
}

interface StorageCache {
    totalSize: number;
    itemSizes: Map<string, number>;
    lastCalculated: number;
    isValid: boolean;
}

// ===========================
// In-Memory Cache
// ===========================

/**
 * Cache for storage size tracking
 * This allows O(1) size updates instead of O(n) recalculation
 */
const storageCache: StorageCache = {
    totalSize: 0,
    itemSizes: new Map(),
    lastCalculated: 0,
    isValid: false,
};

/**
 * Calculate size of a key-value pair in bytes
 */
function calculateItemSize(key: string, value: string): number {
    // Each character is 2 bytes in JavaScript (UTF-16)
    return (key.length + value.length) * 2;
}

/**
 * Check if cache needs refresh
 */
function shouldRefreshCache(): boolean {
    if (!storageCache.isValid) return true;
    const now = Date.now();
    return now - storageCache.lastCalculated > CACHE_INVALIDATION_INTERVAL;
}

/**
 * Initialize or refresh the storage cache
 * This is the only O(n) operation - called sparingly
 */
function refreshCache(): void {
    try {
        let totalSize = 0;
        const itemSizes = new Map<string, number>();
        const keys = Object.keys(sessionStorage);

        for (const key of keys) {
            const value = sessionStorage.getItem(key);
            if (value !== null) {
                const size = calculateItemSize(key, value);
                itemSizes.set(key, size);
                totalSize += size;
            }
        }

        storageCache.totalSize = totalSize;
        storageCache.itemSizes = itemSizes;
        storageCache.lastCalculated = Date.now();
        storageCache.isValid = true;

        logger.debug('[Storage Cache] Refreshed', {
            items: itemSizes.size,
            totalSize: `${(totalSize / 1024).toFixed(2)}KB`,
        });
    } catch (error) {
        logger.error('[Storage Cache] Failed to refresh', error);
        storageCache.isValid = false;
    }
}

/**
 * Update cache when an item is added or modified
 * O(1) operation
 */
function updateCacheOnSet(key: string, value: string): void {
    if (!storageCache.isValid) {
        // Cache not initialized yet, skip update
        return;
    }

    const newSize = calculateItemSize(key, value);
    const oldSize = storageCache.itemSizes.get(key) || 0;

    // Update item size map
    storageCache.itemSizes.set(key, newSize);

    // Update total size incrementally
    storageCache.totalSize = storageCache.totalSize - oldSize + newSize;
}

/**
 * Update cache when an item is removed
 * O(1) operation
 */
function updateCacheOnRemove(key: string): void {
    if (!storageCache.isValid) {
        return;
    }

    const oldSize = storageCache.itemSizes.get(key) || 0;

    if (oldSize > 0) {
        // Update total size incrementally
        storageCache.totalSize -= oldSize;
        storageCache.itemSizes.delete(key);
    }
}

/**
 * Invalidate cache (force refresh on next access)
 */
function invalidateCache(): void {
    storageCache.isValid = false;
}

// ===========================
// Storage Analysis (OPTIMIZED)
// ===========================

/**
 * Get current storage usage statistics
 * OPTIMIZED: Uses cached values, only recalculates when necessary
 *
 * Performance:
 * - Before: O(n) every call
 * - After: O(1) most calls, O(n) only on cache miss/expiry
 */
export function getStorageUsage(): StorageUsage {
    try {
        // Refresh cache if needed (rare O(n) operation)
        if (shouldRefreshCache()) {
            refreshCache();
        }

        const used = storageCache.totalSize;
        const percentage = (used / STORAGE_QUOTA_ESTIMATE) * 100;

        return {
            used,
            total: STORAGE_QUOTA_ESTIMATE,
            percentage,
            isNearLimit: percentage >= WARNING_THRESHOLD * 100,
            isAtLimit: percentage >= EMERGENCY_THRESHOLD * 100,
        };
    } catch (error) {
        logger.error('[Storage] Failed to calculate usage', error);
        invalidateCache();
        return {
            used: 0,
            total: STORAGE_QUOTA_ESTIMATE,
            percentage: 0,
            isNearLimit: false,
            isAtLimit: false,
        };
    }
}

/**
 * Get all conversation entries with metadata
 * OPTIMIZED: Uses cached sizes instead of recalculating
 */
function getConversationEntries(): ConversationEntry[] {
    const entries: ConversationEntry[] = [];

    try {
        // Ensure cache is fresh
        if (shouldRefreshCache()) {
            refreshCache();
        }

        const keys = Object.keys(sessionStorage);

        for (const key of keys) {
            const isConversationKey = CONVERSATION_KEY_PREFIXES.some(prefix => key.startsWith(prefix));
            if (!isConversationKey) continue;

            // Get size from cache instead of recalculating
            const size = storageCache.itemSizes.get(key) || 0;
            if (size === 0) continue;

            let timestamp = Date.now();

            // Try to extract timestamp from stored data
            try {
                const value = sessionStorage.getItem(key);
                if (value) {
                    const parsed = JSON.parse(value);
                    if (parsed.timestamp && typeof parsed.timestamp === 'string') {
                        timestamp = new Date(parsed.timestamp as string).getTime();
                    }
                }
            } catch {
                // Not JSON or no timestamp, use current time
            }

            entries.push({ key, timestamp, size });
        }
    } catch (error) {
        logger.error('[Storage] Failed to get conversation entries', error);
    }

    return entries;
}

// ===========================
// Cleanup Functions
// ===========================

/**
 * Clean up old conversation data based on LRU
 * Keeps only the most recent MAX_CONVERSATIONS
 */
export function cleanupOldConversations(): { removed: number; freedBytes: number } {
    try {
        const entries = getConversationEntries();

        if (entries.length <= MAX_CONVERSATIONS * 2) {
            // Under limit (each conversation has 2 keys: userMessage + correlation)
            return { removed: 0, freedBytes: 0 };
        }

        // Sort by timestamp (oldest first)
        entries.sort((a, b) => a.timestamp - b.timestamp);

        // Calculate how many to remove (keep last MAX_CONVERSATIONS * 2)
        const targetCount = MAX_CONVERSATIONS * 2;
        const toRemove = entries.slice(0, entries.length - targetCount);

        let freedBytes = 0;
        let removed = 0;

        for (const entry of toRemove) {
            try {
                sessionStorage.removeItem(entry.key);
                // Update cache incrementally
                updateCacheOnRemove(entry.key);
                freedBytes += entry.size;
                removed++;
            } catch (error) {
                logger.error(`[Storage] Failed to remove key ${entry.key}`, error);
            }
        }

        if (removed > 0) {
            logger.warn(`[Storage] Cleaned up ${removed} old entries, freed ${(freedBytes / 1024).toFixed(2)}KB`);
        }

        return { removed, freedBytes };
    } catch (error) {
        logger.error('[Storage] Cleanup failed', error);
        return { removed: 0, freedBytes: 0 };
    }
}

/**
 * Emergency cleanup - removes oldest 50% of conversations
 */
export function emergencyCleanup(): { removed: number; freedBytes: number } {
    try {
        const entries = getConversationEntries();

        if (entries.length === 0) {
            return { removed: 0, freedBytes: 0 };
        }

        // Sort by timestamp (oldest first)
        entries.sort((a, b) => a.timestamp - b.timestamp);

        // Remove oldest 50%
        const toRemove = entries.slice(0, Math.ceil(entries.length / 2));

        let freedBytes = 0;
        let removed = 0;

        for (const entry of toRemove) {
            try {
                sessionStorage.removeItem(entry.key);
                // Update cache incrementally
                updateCacheOnRemove(entry.key);
                freedBytes += entry.size;
                removed++;
            } catch (error) {
                logger.error(`[Storage] Failed to remove key ${entry.key}`, error);
            }
        }

        logger.warn(
            `[Storage] Emergency cleanup: removed ${removed} entries, freed ${(freedBytes / 1024).toFixed(2)}KB`
        );

        return { removed, freedBytes };
    } catch (error) {
        logger.error('[Storage] Emergency cleanup failed', error);
        return { removed: 0, freedBytes: 0 };
    }
}

/**
 * Clear all conversation data from sessionStorage
 */
export function clearAllConversations(): number {
    try {
        const entries = getConversationEntries();
        let removed = 0;

        for (const entry of entries) {
            try {
                sessionStorage.removeItem(entry.key);
                // Update cache incrementally
                updateCacheOnRemove(entry.key);
                removed++;
            } catch (error) {
                logger.error(`[Storage] Failed to remove key ${entry.key}`, error);
            }
        }

        if (removed > 0) {
            logger.warn(`[Storage] Cleared all ${removed} conversation entries`);
        }

        return removed;
    } catch (error) {
        logger.error('[Storage] Failed to clear conversations', error);
        return 0;
    }
}

// ===========================
// Safe Storage Operations (OPTIMIZED)
// ===========================

/**
 * Safely set item in sessionStorage with automatic cleanup on quota exceeded
 * OPTIMIZED: Updates cache incrementally instead of recalculating
 */
export function safeSetItem(key: string, value: string, retryOnQuota = true): boolean {
    try {
        // Check if we're near the limit before attempting
        const usage = getStorageUsage(); // Now O(1) instead of O(n)

        if (usage.isAtLimit) {
            logger.warn('[Storage] At storage limit, performing emergency cleanup');
            emergencyCleanup();
        } else if (usage.isNearLimit) {
            logger.warn('[Storage] Near storage limit, cleaning up old conversations');
            cleanupOldConversations();
        }

        // Attempt to store
        sessionStorage.setItem(key, value);

        // Update cache incrementally (O(1))
        updateCacheOnSet(key, value);

        return true;
    } catch (error) {
        if (error instanceof Error && error.name === 'QuotaExceededError') {
            logger.error('[Storage] QuotaExceededError', error);

            if (retryOnQuota) {
                // Show user message
                message.warning({
                    content: 'Storage limit reached. Clearing old conversations...',
                    key: 'storage-quota-warning',
                    duration: 3,
                });

                // Emergency cleanup
                const result = emergencyCleanup();

                if (result.removed > 0) {
                    message.success({
                        content: `Cleared ${result.removed} old conversations. Please try again.`,
                        key: 'storage-quota-success',
                        duration: 2,
                    });

                    // Retry once
                    try {
                        sessionStorage.setItem(key, value);
                        // Update cache on successful retry
                        updateCacheOnSet(key, value);
                        return true;
                    } catch (retryError) {
                        logger.error('[Storage] Retry failed', retryError);
                        message.error('Unable to save. Please refresh the page.');
                        invalidateCache(); // Invalidate on error
                        return false;
                    }
                } else {
                    message.error('Storage cleanup failed. Please refresh the page.');
                    invalidateCache();
                    return false;
                }
            }

            invalidateCache();
            return false;
        }

        logger.error('[Storage] Failed to set item', error);
        invalidateCache();
        return false;
    }
}

/**
 * Safe remove item from sessionStorage
 * NEW: Helper to ensure cache stays in sync
 */
export function safeRemoveItem(key: string): boolean {
    try {
        sessionStorage.removeItem(key);
        updateCacheOnRemove(key);
        return true;
    } catch (error) {
        logger.error('[Storage] Failed to remove item', error);
        invalidateCache();
        return false;
    }
}

/**
 * Handle QuotaExceededError with user feedback
 */
export function handleQuotaExceeded(): void {
    message.warning({
        content: 'Storage quota reached. Clearing old conversations...',
        key: 'quota-warning',
        duration: 3,
    });

    const result = emergencyCleanup();

    if (result.removed > 0) {
        message.success({
            content: `Cleared ${result.removed} old conversations. Freed ${(result.freedBytes / 1024).toFixed(2)}KB.`,
            key: 'quota-success',
            duration: 3,
        });
    } else {
        message.error({
            content: 'Unable to free up space. Please refresh the page.',
            key: 'quota-error',
            duration: 5,
        });
    }
}

// ===========================
// Monitoring
// ===========================

/**
 * Log storage usage statistics (for development)
 */
export function logStorageStats(): void {
    if (process.env.NODE_ENV !== 'development') return;

    const usage = getStorageUsage();
    const entries = getConversationEntries();

    logger.debug('[Storage Stats]', {
        used: `${(usage.used / 1024).toFixed(2)}KB`,
        total: `${(usage.total / 1024).toFixed(2)}KB`,
        percentage: `${usage.percentage.toFixed(2)}%`,
        conversations: entries.length / 2, // Divide by 2 (userMessage + correlation)
        isNearLimit: usage.isNearLimit,
        isAtLimit: usage.isAtLimit,
        cacheAge: `${((Date.now() - storageCache.lastCalculated) / 1000).toFixed(0)}s`,
        cacheValid: storageCache.isValid,
    });
}

/**
 * Show warning if storage is near limit
 */
export function checkAndWarnStorageUsage(): void {
    const usage = getStorageUsage(); // Now O(1)

    if (usage.isAtLimit) {
        message.error({
            content: `Storage is ${usage.percentage.toFixed(0)}% full. Old conversations will be automatically removed.`,
            key: 'storage-warning',
            duration: 5,
        });
        emergencyCleanup();
    } else if (usage.isNearLimit) {
        message.warning({
            content: `Storage is ${usage.percentage.toFixed(0)}% full. Consider clearing old conversations.`,
            key: 'storage-warning',
            duration: 4,
        });
        cleanupOldConversations();
    }
}

/**
 * Force cache refresh (useful after external modifications)
 */
export function forceRefreshCache(): void {
    invalidateCache();
    refreshCache();
}

// ===========================
// Exports
// ===========================

export const sessionStorageManager = {
    getUsage: getStorageUsage,
    cleanup: cleanupOldConversations,
    emergencyCleanup,
    clearAll: clearAllConversations,
    safeSet: safeSetItem,
    safeRemove: safeRemoveItem,
    handleQuotaExceeded,
    logStats: logStorageStats,
    checkAndWarn: checkAndWarnStorageUsage,
    forceRefresh: forceRefreshCache,
    invalidateCache,
};

export default sessionStorageManager;
